import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchStats } from "../../redux/dashboardSlice";
import { fetchCampuses, fetchCampusClassesDetail } from "../../redux/campusesSlice";
import Card from "../../Components/admin/Cards/Cards";
// Graphs hidden on home page per request
// import LineChartComponent from "../../Components/admin/Cards/LineChartComponent";
// import AreaChartComponent from "../../Components/admin/Cards/AreaChartComponent";
// import AcademicResultsChart from "../../Components/admin/Cards/AcademicResultsChart";
// import AttendanceChart from "../../Components/admin/Cards/AttendanceChart";
import WelcomeSection from "../../Components/Common/WelcomeSection";
// import TeacherSatisfactionChart from "../../Components/admin/Cards/TeacherSatisfactionChart";
// import VolumesChart from "../../Components/admin/Cards/VolumesChart";
// import FinancialChart from "../../Components/admin/Cards/FinancialChart";
import CustomDropdown from "../../Components/admin/CustomDropdown";
// import { chartData } from "../../Constant/charts";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";

const Dashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { stats, loading: statsLoading, error: statsError } = useSelector((state) => state.dashboard);
  const { 
    campuses, 
    campusClassesDetail, 
    loading: campusesLoading 
  } = useSelector((state) => state.campuses);

  const [campusFilter, setCampusFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Available classes and sections based on selected campus
  const [availableClasses, setAvailableClasses] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);

  // Get IDs for selected filters
  const getSelectedCampusId = useCallback(() => {
    if (!campusFilter) return null;
    const campus = campuses.find(c => c.name === campusFilter);
    return campus?.id || null;
  }, [campusFilter, campuses]);

  const getSelectedClassId = useCallback(() => {
    if (!classFilter || !campusClassesDetail) return null;
    
    for (const campus of campusClassesDetail) {
      const foundClass = campus.classes?.find(cls => cls.className === classFilter);
      if (foundClass) {
        return foundClass.id || foundClass.classId || null;
      }
    }
    return null;
  }, [classFilter, campusClassesDetail]);

  const getSelectedSectionId = useCallback(() => {
    if (!sectionFilter || !campusClassesDetail) return null;
    
    for (const campus of campusClassesDetail) {
      for (const cls of campus.classes || []) {
        const foundSection = cls.sections?.find(section => section.sectionName === sectionFilter);
        if (foundSection) {
          return foundSection.id || foundSection.sectionId || null;
        }
      }
    }
    return null;
  }, [sectionFilter, campusClassesDetail]);

  // Debounced function to fetch stats with filters
  const debouncedFetchStats = useCallback(
    debounce((filters) => {
      dispatch(fetchStats(filters));
    }, 500),
    [dispatch]
  );

  // Function to fetch stats with current filters
  const fetchStatsWithFilters = useCallback(() => {
    const filters = {
      campusId: getSelectedCampusId(),
      classId: getSelectedClassId(),
      sectionId: getSelectedSectionId(),
      startDate: startDate || null,
      endDate: endDate || null,
    };

    // Remove null/empty values
    const cleanFilters = Object.keys(filters).reduce((acc, key) => {
      if (filters[key] !== null && filters[key] !== "") {
        acc[key] = filters[key];
      }
      return acc;
    }, {});

    debouncedFetchStats(cleanFilters);
  }, [getSelectedCampusId, getSelectedClassId, getSelectedSectionId, startDate, endDate, debouncedFetchStats]);

  // Function to reset all filters
  const resetFilters = useCallback(() => {
    setCampusFilter("");
    setClassFilter("");
    setSectionFilter("");
    setStartDate("");
    setEndDate("");
    
    // Fetch stats without any filters
    dispatch(fetchStats());
  }, [dispatch]);

  // Initial data fetch
  useEffect(() => {
    dispatch(fetchCampuses());
    dispatch(fetchCampusClassesDetail());
    // Initial stats fetch without filters
    dispatch(fetchStats());
  }, [dispatch]);

  // Fetch stats when filters change
  useEffect(() => {
    if (campuses.length > 0 && campusClassesDetail.length > 0) {
      fetchStatsWithFilters();
    }
  }, [campusFilter, classFilter, sectionFilter, startDate, endDate, fetchStatsWithFilters, campuses.length, campusClassesDetail.length]);

  // Update available classes when campus is selected
  useEffect(() => {
    if (campusFilter && campusClassesDetail && campusClassesDetail.length > 0) {
      const selectedCampus = campusClassesDetail.find(campus => campus.name === campusFilter);
      if (selectedCampus && selectedCampus.classes) {
        setAvailableClasses(selectedCampus.classes.map(cls => cls.className));
        setClassFilter(""); // Reset class filter
        setSectionFilter(""); // Reset section filter
        setAvailableSections([]); // Reset available sections
      } else {
        setAvailableClasses([]);
      }
    } else {
      // If no campus is selected, show all classes from all campuses
      const allClasses = [];
      campusClassesDetail?.forEach(campus => {
        campus.classes?.forEach(cls => {
          if (!allClasses.includes(cls.className)) {
            allClasses.push(cls.className);
          }
        });
      });
      setAvailableClasses(allClasses);
    }
  }, [campusFilter, campusClassesDetail]);

  // Update available sections when class is selected
  useEffect(() => {
    if (classFilter && campusClassesDetail && campusClassesDetail.length > 0) {
      let sections = [];
      
      // If campus is selected, filter by both campus and class
      if (campusFilter) {
        const selectedCampus = campusClassesDetail.find(campus => campus.name === campusFilter);
        if (selectedCampus) {
          const selectedClass = selectedCampus.classes?.find(cls => cls.className === classFilter);
          if (selectedClass) {
            sections = selectedClass.sections?.map(section => section.sectionName) || [];
          }
        }
      } else {
        // If no campus selected, get sections from all campuses for the selected class
        campusClassesDetail?.forEach(campus => {
          const selectedClass = campus.classes?.find(cls => cls.className === classFilter);
          if (selectedClass) {
            selectedClass.sections?.forEach(section => {
              if (!sections.includes(section.sectionName)) {
                sections.push(section.sectionName);
              }
            });
          }
        });
      }
      
      setAvailableSections(sections);
      setSectionFilter(""); // Reset section filter
    } else {
      // If no class is selected, collect all sections from all classes
      const allSections = [];
      campusClassesDetail?.forEach(campus => {
        campus.classes?.forEach(cls => {
          cls.sections?.forEach(section => {
            if (!allSections.includes(section.sectionName)) {
              allSections.push(section.sectionName);
            }
          });
        });
      });
      setAvailableSections(allSections);
    }
  }, [classFilter, campusFilter, campusClassesDetail]);

  // Get campus names for dropdown
  const campusOptions = campuses.map(campus => campus.name);

  const cardData = stats
    ? [
        {
          title: t("teachers"),
          value: stats.totalTeachers || 0,
          percent: `${stats.percentageIncreaseTeachers || 0}%`,
          color: "bg-gradient-to-t from-[#241763] via-[#3c2a8a] to-[#5b4b9a]",
          increase: t("SinceLastPeriod"),
          progressValue: stats.percentageIncreaseTeachers || 0,
        },
        {
          title: t("students"),
          value: stats.totalStudents || 0,
          percent: `${stats.percentageIncreaseStudents || 0}%`,
          color: "bg-gradient-to-t from-[#241763] via-[#3c2a8a] to-[#5b4b9a]",
          increase: t("SinceLastPeriod"),
          progressValue: stats.percentageIncreaseStudents || 0,
        },
        {
          title: t("courses"),
          value: stats.totalCourses || 0,
          percent: `${stats.percentageIncreaseCourses || 0}%`,
          color: "bg-gradient-to-t from-[#241763] via-[#3c2a8a] to-[#5b4b9a]",
          increase: t("SinceLastPeriod"),
          progressValue: stats.percentageIncreaseCourses || 0,
        },
        {
          title: t("FeeManagement"),
          value: `$${stats.totalFeeCollected?.toLocaleString() || 0}`,
          percent: `${stats.percentageIncreaseFeeCollected || 0}%`,
          color: "bg-gradient-to-t from-[#241763] via-[#3c2a8a] to-[#5b4b9a]",
          increase: t("SinceLastPeriod"),
          progressValue: stats.percentageIncreaseFeeCollected || 0,
        },
      ]
    : [];

  // Display a loading message if either stats or campuses are loading
  if (statsLoading || campusesLoading) {
    return <div>{t("Loading")}...</div>;
  }

  if (statsError) {
    return <div>{t("Error")}: {statsError}</div>;
  }

  return (
    <div className="p-6">
      <WelcomeSection />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 my-8">
        {cardData.map((card, index) => (
          <Card
            key={index}
            title={card.title}
            value={card.value}
            percent={card.percent}
            color={card.color}
            progressValue={card.progressValue}
            increase={card.increase}
          />
        ))}
      </div>

      {/* Filter Section */}
      <div className="flex flex-col lg:flex-row justify-between items-end mb-6 w-full space-y-4 lg:space-y-0 lg:space-x-4">
        {/* Campus Filter */}
        <div className="w-full lg:w-1/4">
          <CustomDropdown
            options={campusOptions}
            value={campusFilter}
            onChange={(value) => setCampusFilter(value)}
            placeholder={t("Campuses")}
          />
        </div>

        {/* Class Filter */}
        <div className="w-full lg:w-1/4">
          <CustomDropdown
            options={availableClasses}
            value={classFilter}
            onChange={(value) => setClassFilter(value)}
            placeholder={t("allClasses")}
            disabled={availableClasses.length === 0}
          />
        </div>

        {/* Section Filter */}
        <div className="w-full lg:w-1/4">
          <CustomDropdown
            options={availableSections}
            value={sectionFilter}
            onChange={(value) => setSectionFilter(value)}
            placeholder={t("allSections")}
            disabled={availableSections.length === 0}
          />
        </div>

       
        {/* <div className="w-full lg:w-1/6">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t("Start Date")}
          />
        </div>

        
        <div className="w-full lg:w-1/6">
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t("End Date")}
          />
        </div> */}

        {/* Reset Button */}
        <div className="w-full lg:w-1/4">
          <button
            onClick={resetFilters}
            className="w-full px-4 py-2 bg-gradient-to-t from-[#241763] via-[#3c2a8a] to-[#5b4b9a] text-white font-medium rounded-md border border-[#6b5b9a] hover:shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#6b5b9a] focus:ring-offset-2"
          >
            {t("Reset")}
          </button>
        </div>
      </div>

      {/* All home page graphs hidden per request
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FinancialChart 
          campus={campusFilter}
          class={classFilter}
          section={sectionFilter}
        />
        <VolumesChart 
          filter="all" 
          campus={campusFilter}
          class={classFilter}
          section={sectionFilter}
        />

        <AcademicResultsChart 
          campus={campusFilter}
          class={classFilter}
          section={sectionFilter}
        />
        <AttendanceChart 
          title={t("StudentAttendance")} 
          filter="all"
          campus={campusFilter}
          class={classFilter}
          section={sectionFilter}
        />
        <AttendanceChart 
          title={t("TeacherAttendance")} 
          filter="all"
          campus={campusFilter}
          class={classFilter}
          section={sectionFilter}
        />
        <TeacherSatisfactionChart 
          campus={campusFilter}
          class={classFilter}
          section={sectionFilter}
        />
        <LineChartComponent 
          data={chartData}
          campus={campusFilter}
          class={classFilter}
          section={sectionFilter}
        />
        <AreaChartComponent 
          data={chartData}
          campus={campusFilter}
          class={classFilter}
          section={sectionFilter}
        />
      </div>
      */}
    </div>
  );
};

export default Dashboard;